import { useEffect, useState } from "react";
import { useMotion } from "./MotionProvider";
import { MotionCanvas, seeded } from "./primitives";
import { Logo } from "../Logo";

// 13 constellation — gold particles with links (deterministic layout)
export function Constellation() {
  const mobile = typeof window !== "undefined" && window.innerWidth < 768;
  const count = mobile ? 28 : 56;
  const draw = (ctx, t, s) => {
    if (!s.init) {
      const r = seeded(7);
      s.p = Array.from({ length: count }, () => ({ x: r() * s.w, y: r() * s.h, vx: (r() - 0.5) * 0.18, vy: (r() - 0.5) * 0.18 }));
      s.init = true;
    }
    ctx.clearRect(0, 0, s.w, s.h);
    for (const p of s.p) {
      if (t) { p.x += p.vx; p.y += p.vy; if (p.x < 0 || p.x > s.w) p.vx *= -1; if (p.y < 0 || p.y > s.h) p.vy *= -1; }
    }
    for (let i = 0; i < s.p.length; i++) {
      const a = s.p[i];
      for (let j = i + 1; j < s.p.length; j++) {
        const b = s.p[j];
        const d = Math.hypot(a.x - b.x, a.y - b.y);
        if (d < 120) { ctx.strokeStyle = `rgba(212,175,55,${0.16 * (1 - d / 120)})`; ctx.lineWidth = 1; ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y); ctx.stroke(); }
      }
      ctx.fillStyle = "rgba(232,201,106,0.7)"; ctx.beginPath(); ctx.arc(a.x, a.y, 1.3, 0, Math.PI * 2); ctx.fill();
    }
  };
  return <div className="absolute inset-0 motion-decor" data-testid="constellation-bg"><MotionCanvas n={13} draw={draw} className="absolute inset-0" /></div>;
}

// 27 mirror reflection — gold light pillars reflected on a rippling floor
export function MirrorReflection() {
  const draw = (ctx, t, s) => {
    const { w, h } = s;
    ctx.clearRect(0, 0, w, h);
    const horizon = h * 0.55;
    const bars = 26;
    for (let i = 0; i < bars; i++) {
      const x = (i + 0.5) * (w / bars);
      const bh = (0.25 + 0.75 * Math.abs(Math.sin(i * 1.7 + 0.6))) * horizon * 0.55;
      const g = ctx.createLinearGradient(0, horizon - bh, 0, horizon);
      g.addColorStop(0, "rgba(212,175,55,0)"); g.addColorStop(1, "rgba(232,201,106,0.55)");
      ctx.fillStyle = g; ctx.fillRect(x - 1.5, horizon - bh, 3, bh);
      for (let y = 0; y < bh * 0.8; y += 3) {
        const off = Math.sin(y * 0.12 + t * 0.002 + i) * (1 + y * 0.03);
        ctx.fillStyle = `rgba(212,175,55,${0.35 * (1 - y / (bh * 0.8))})`;
        ctx.fillRect(x - 1.5 + off, horizon + y, 3, 2);
      }
    }
    const hl = ctx.createLinearGradient(0, 0, w, 0);
    hl.addColorStop(0, "rgba(212,175,55,0)"); hl.addColorStop(0.5, "rgba(232,201,106,0.6)"); hl.addColorStop(1, "rgba(212,175,55,0)");
    ctx.fillStyle = hl; ctx.fillRect(0, horizon, w, 1);
  };
  return <div className="absolute inset-0 motion-decor" data-testid="mirror-reflection"><MotionCanvas n={27} draw={draw} className="absolute inset-0" /></div>;
}

// 16 headline rise — words rise in sequence
export function HeadlineRise({ lines, className = "" }) {
  const { allows } = useMotion();
  let n = 0;
  return (
    <h1 className={className} data-testid="headline-rise">
      {lines.map((ln, li) => (
        <span key={li} className={`block ${ln.className || ""}`}>
          {ln.text.split(" ").map((w) => { n += 1; return (
            <span key={`${li}-${n}`} className="inline-block overflow-hidden align-bottom pb-[0.08em]">
              <span className={`inline-block ${ln.className?.includes("text-gold") ? "text-gold" : ""} ${allows(16) ? "m-rise" : ""}`} style={{ animationDelay: `${120 + n * 70}ms` }}>{w}&nbsp;</span>
            </span>); })}
        </span>))}
    </h1>
  );
}

// 01 logo reveal splash (once per session, ≤1.8s, click to skip) over a 15 horizon grid
export function LogoReveal() {
  const { mode } = useMotion();
  const [on, setOn] = useState(() => mode !== "off" && !sessionStorage.getItem("pd_splash"));
  useEffect(() => {
    if (!on) return undefined;
    sessionStorage.setItem("pd_splash", "1");
    const t = setTimeout(() => setOn(false), 1800);
    return () => clearTimeout(t);
  }, [on]);
  if (!on) return null;
  return (
    <div className="fixed inset-0 z-[100] grid place-items-center bg-[#050507] m-splash cursor-pointer" onClick={() => setOn(false)} data-testid="logo-reveal-splash">
      <HorizonGrid />
      <div className="relative flex flex-col items-center gap-4">
        <span className="m-logo-in"><Logo size={56} word={false} /></span>
        <span className="font-display font-extrabold text-2xl tracking-tight text-[#f4efe4] m-rise" style={{ animationDelay: "380ms" }}>Paper<span className="text-gold">desk</span></span>
        <span className="h-px w-40 bg-gradient-to-r from-transparent via-[hsl(var(--gold))] to-transparent m-line" />
      </div>
    </div>
  );
}

// 15 horizon grid — perspective floor of gold lines
export function HorizonGrid() {
  return (
    <div className="absolute inset-x-0 bottom-0 h-1/2 overflow-hidden [perspective:400px] motion-decor" aria-hidden="true" data-testid="horizon-grid">
      <div className="absolute inset-x-[-50%] top-0 h-[200%] m-grid origin-top"
        style={{ transform: "rotateX(62deg)", backgroundImage: "linear-gradient(hsl(var(--gold)/0.35) 1px, transparent 1px), linear-gradient(90deg, hsl(var(--gold)/0.35) 1px, transparent 1px)", backgroundSize: "48px 48px", maskImage: "linear-gradient(to bottom, black, transparent 70%)" }} />
      <div className="absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-[#050507] to-transparent" />
    </div>
  );
}
